"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CircleCheck, ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  title: "Background Checks",
  description: "Official criminal history verification and employment screening services conducted with the highest level of accuracy and confidentiality.",
  features: ["State and federal database searches", "Certified fingerprinting services", "Expedited processing for official use"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_background_check.png",
  link: "/PublicServicesPage"
}, {
  title: "Firearm Permits",
  description: "Comprehensive licensing and registration services for residents, ensuring all legal requirements and safety standards are met.",
  features: ["New application processing", "Renewal and transfer assistance", "Safety certification verification"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_firearm_permit.png",
  link: "/PublicServicesPage"
}, {
  title: "Public Records Request",
  description: "Transparent access to incident reports, accident documentation, and department records in compliance with freedom of information laws.",
  features: ["Online report retrieval system", "Certified document copies", "Redaction of sensitive information"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_records_archive.png",
  link: "/TransparencyPage"
}, {
  title: "Community Alerts",
  description: "Real-time notification systems designed to keep citizens informed about local safety concerns, traffic disruptions, and emergency events.",
  features: ["SMS and email alert registration", "Neighborhood crime mapping", "Emergency broadcast integration"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_emergency_alert.png",
  link: "/HomePage"
}, {
  title: "Victim Advocacy",
  description: "Dedicated support services providing guidance, resources, and protection for individuals affected by crime within our jurisdiction.",
  features: ["Crisis intervention and support", "Legal system navigation aid", "Referrals to specialized counseling"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/police_victim_support.png",
  link: "/ContactPage"
}];
function ServiceBlock({
  service,
  index
}: {
  service: typeof servicesData[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const isReversed = index % 2 !== 0;
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`relative w-full aspect-square max-w-md mx-auto lg:max-w-none ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-secondary/5 to-primary/10 rounded-lg transform rotate-1 scale-105 -z-10 transition-transform duration-700 hover:rotate-2 hover:scale-105"></div>
        <div className="absolute inset-0 bg-card/80 backdrop-blur-xl rounded-lg border border-border shadow-md -z-10"></div>
        <div className="h-full w-full flex items-center justify-center p-8">
          <img src={service.image} alt={`Official representation of: ${service.title}`} className="w-full h-full object-contain drop-shadow-xl animate-in fade-in zoom-in duration-1000" loading="lazy" />
        </div>
      </div>

      {}
      <div className={`flex flex-col gap-8 ${isReversed ? "lg:order-1" : "lg:order-2"}`}>
        <div className="space-y-4">
          <div className="inline-flex items-center rounded-md border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-default font-semibold text-primary uppercase tracking-wider">
            <span className="flex h-2 w-2 rounded-lg bg-primary mr-2"></span>
            Service {index + 1}
          </div>
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            {service.title}
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            {service.description}
          </p>
        </div>

        <ul className="space-y-4">
          {service.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex items-start gap-3">
              <CircleCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" />
              <span className="text-foreground font-default font-medium">{feature}</span>
            </li>)}
        </ul>

        <div className="pt-4">
          <Button asChild size="lg" className="group rounded-md px-8 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
            <Link to={service.link}>
              View Details
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function OurPublicCommitments() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224262" id="our-public-commitments" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 lg:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tight text-foreground mb-6">
            Our Public <span className="text-primary">Commitments</span>
          </h2>
          <p className="text-xl text-muted-foreground font-default">
            The Metropolitan Central Police is dedicated to providing transparent, efficient, and accessible services to ensure the safety and well-being of our community.
          </p>
        </div>

        {}
        <div className="flex flex-col gap-24 lg:gap-40">
          {servicesData.map((service, index) => <ServiceBlock key={index} service={service} index={index} />)}
        </div>

      </div>
    </section>;
}